import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { BASE_URL } from 'utils/constants';

const PortfolioSearch = () => {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [portfolio, setPortfolio] = useState(null);

    const words = useMemo(() => Array.from(new Set(
        term.trim().toLocaleLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean)
    )), [term]);

    useEffect(() => {
        const query = term.trim();
        if (query.length < 2) {
            setResults([]);
            setError('');
            setLoading(false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setError('');
        const timer = window.setTimeout(() => {
            axios.get(`${BASE_URL}/capstone/portfolio/searchportfolio.php`, { params: { keywords: query } })
                .then(res => {
                    if (active) setResults(res?.data?.data?.results ?? []);
                })
                .catch(() => {
                    if (active) {
                        setResults([]);
                        setError('Unable to search portfolios. Please try again.');
                    }
                })
                .finally(() => {
                    if (active) setLoading(false);
                });
        }, 300);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [term]);

    const highlight = value => {
        if (!words.length) return value;
        const escaped = words.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const parts = value.split(new RegExp(`(${escaped.join('|')})`, 'giu'));
        const wordSet = new Set(words);
        return parts.map((part, index) => wordSet.has(part.toLocaleLowerCase())
            ? <mark key={index}>{part}</mark>
            : part);
    };

    const viewPortfolio = teamId => {
        Swal.showLoading();
        axios.get(`${BASE_URL}/capstone/portfolio/searchportfolio.php`, { params: { team_id: teamId } })
            .then(res => {
                setPortfolio(res?.data?.data?.portfolio ?? null);
                Swal.close();
            })
            .catch(err => Swal.fire(err?.response?.data?.msg || 'Unable to load portfolio', '', 'error'));
    };

    if (portfolio) {
        return (
            <div className="mt-4">
                <button type="button" className="btn btn-secondary mb-3" onClick={() => setPortfolio(null)}>Back to Search</button>
                <h2>{portfolio.team_number ? `#${portfolio.team_number} - ` : ''}{portfolio.team_name}</h2>
                {portfolio.theme_name && <p className="text-muted">{portfolio.theme_name}</p>}
                {portfolio.sections.map(section => (
                    <div className="card mb-3" key={section.section_key}>
                        <div className="card-body">
                            <h4>{section.section_title}</h4>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{section.content || 'No content provided'}</p>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="mt-4">
            <h2>Search Portfolios</h2>
            <label htmlFor="portfolioSearchInput">Search portfolio keywords:</label>
            <input
                id="portfolioSearchInput"
                type="search"
                className="form-control"
                placeholder="Enter at least two characters..."
                value={term}
                onChange={event => setTerm(event.target.value)}
            />
            {term.trim().length >= 2 && (
                <div className="border rounded mt-2 p-3 bg-light">
                    {loading ? <div>Searching portfolios...</div>
                        : error ? <div className="text-danger">{error}</div>
                            : results.length === 0 ? <div>No portfolio matches found</div>
                                : results.map(result => (
                                    <div key={result.team_id} className="card mb-3">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start flex-wrap">
                                                <div>
                                                    <h5>{result.team_number ? `#${result.team_number} - ` : ''}{result.team_name}</h5>
                                                    <div className="text-muted mb-2">{result.theme_name}</div>
                                                </div>
                                                <button type="button" className="btn btn-sm btn-primary" onClick={() => viewPortfolio(result.team_id)}>View Portfolio</button>
                                            </div>
                                            {result.matches?.map(match => (
                                                <div key={match.section_key} className="mt-2">
                                                    <strong>{match.section_title}</strong>
                                                    <div>{highlight(match.snippet)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                </div>
            )}
        </div>
    );
};

export default PortfolioSearch;
